import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Switch } from '@frontend/shadcn/components/ui/switch';
import { Button } from '@frontend/shadcn/components/ui/button';
import { TAutomation, useDisableAutomationMutation, useResumeAutomationMutation } from '@frontend/app/hooks';
import { AutomationStatus } from '@frontend/app/types/globalTypes';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@frontend/shadcn/components/ui/alert-dialog';

interface AutomationItemProps {
  automation: TAutomation;
  projectId: number;
  refetchAutomations: () => void;
}

export const AutomationItem: React.FC<AutomationItemProps> = ({ automation, projectId, refetchAutomations }) => {
  const [disableAutomation] = useDisableAutomationMutation();
  const [resumeAutomation] = useResumeAutomationMutation();
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [showResumeDialog, setShowResumeDialog] = useState(false);

  const onDisableAutomation = async () => {
    await disableAutomation({ variables: { automationId: automation.id } });
    refetchAutomations();
    setShowDisableDialog(false);
  };

  const onResumeAutomation = async () => {
    await resumeAutomation({ variables: { automationId: automation.id } });
    refetchAutomations();
    setShowResumeDialog(false);
  };

  const handleSwitchChange = (checked: boolean) => {
    if (checked) {
      setShowResumeDialog(true);
    } else {
      setShowDisableDialog(true);
    }
  };

  return (
    <div className="bg-white border border-grey-2 rounded-2xl p-4 flex flex-col gap-2 max-w-[296px] h-[180px] justify-between">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild aria-label={`Automation name: ${automation.name}`}>
                <h3 className="text-base font-medium m-0 truncate max-w-[200px] text-grey-6">
                  {automation.name.length > 20 ? `${automation.name.slice(0, 20)}...` : automation.name}
                </h3>
              </TooltipTrigger>
              <TooltipContent>{automation.name}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <div className="flex items-center gap-2">
            <Switch
              checked={automation.status === AutomationStatus.ACTIVE}
              onCheckedChange={handleSwitchChange}
              className="data-[state=checked]:bg-[#5DB884]"
              id={`automation-switch-${automation.id}`}
              aria-label={`Toggle ${automation.name} automation ${automation.status === AutomationStatus.ACTIVE ? 'off' : 'on'}`}
            />
            <label htmlFor={`automation-switch-${automation.id}`} className="text-grey-6 text-sm">
              On
            </label>
          </div>
        </div>
        <p className="text-grey-5 text-sm m-0 font-regular flex-1">{automation.description}</p>
      </div>

      <Button variant="link" className="text-sm font-medium transition-colors underline p-0 flex justify-start" asChild>
        <Link to={`/projects/${projectId}/automation_detail/automation/${automation.id}`}>View</Link>
      </Button>

      <AlertDialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable Automation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to disable this automation? This will stop the automation from running.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDisableAutomation}>Disable</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showResumeDialog} onOpenChange={setShowResumeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resume Automation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to resume this automation? This will start the automation running again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onResumeAutomation}>Resume</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
